import { apiFetch } from "@/lib/auth-api";

export interface OutboundConnection {
	id: number;
	clientId: number;
	clientName: string;
	name: string;
	dbType: string;
	host: string;
	port: number;
	databaseName: string;
	username: string;
	secretOcid: string;
	sslMode?: string | null;
	active: boolean;
	lastTestedAt?: string | null;
	lastTestStatus?: string | null;
	lastTestError?: string | null;
	notes?: string | null;
}

export interface UpsertOutboundConnectionRequest {
	clientId: number;
	name: string;
	dbType: string;
	host: string;
	port: number;
	databaseName: string;
	username: string;
	secretOcid: string;
	sslMode?: string | null;
	active: boolean;
}

export interface OutboundClient {
	id: number;
	producerId?: number | null;
	name: string;
	producerName?: string | null;
	contractId?: number | null;
	active: boolean;
	notes?: string | null;
	createdAt: string;
	updatedAt: string;
}

export interface OutboundProducer {
	id: number;
	name: string;
}

export interface OutboundVerificationRunSummaryItem {
	streamType: string;
	status: string;
	comparedSourceRows: number;
	missingInDestination: number;
	differentRows: number;
}

export interface OutboundVerificationRunSummary {
	streams: number;
	warnings: number;
	failed: number;
	comparedSourceRows: number;
	missingInDestination: number;
	differentRows: number;
	streamStatuses: OutboundVerificationRunSummaryItem[];
	error?: string;
}

export interface OutboundVerificationRunRow {
	id: number;
	connectionId: number;
	clientId: number;
	contractId: number;
	tenantSchema: string;
	dateFrom: string;
	dateTo: string;
	status: string;
	triggeredByUserId?: number | null;
	startedAt: string;
	finishedAt?: string | null;
	createdAt: string;
	updatedAt: string;
	summary: OutboundVerificationRunSummary;
}

export interface OutboundVerificationResultRow {
	id: number;
	runId: number;
	streamType: string;
	status: string;
	comparedSourceRows: number;
	destinationRowsForSourceKeys: number;
	missingInDestination: number;
	differentRows: number;
	sampleMissing: unknown;
	sampleDifferences: unknown;
	notes: unknown;
	createdAt: string;
	updatedAt: string;
}

export interface OutboundVerificationRunDetail {
	run: OutboundVerificationRunRow;
	results: OutboundVerificationResultRow[];
}

export interface OutboundConnectionTestResult {
	connectionId: number;
	clientName: string;
	connectionName: string;
	dbType: string;
	databaseName: string;
	status: string;
	serverVersion?: string | null;
	error?: string | null;
}

export interface OutboundSchemaTable {
	tableSchema: string;
	tableName: string;
}

export interface OutboundSchemaColumn {
	tableSchema: string;
	tableName: string;
	columnName: string;
	ordinalPosition: number;
	dataType: string;
	isNullable: boolean;
	characterMaximumLength?: number | null;
	numericPrecision?: number | null;
	numericScale?: number | null;
}

export interface OutboundSchemaSnapshot {
	connection: OutboundConnection;
	tables: OutboundSchemaTable[];
	columns: OutboundSchemaColumn[];
}

export interface RunHerbapolVerificationRequest {
	tenantSchema?: string;
	dateFrom?: string;
	dateTo?: string;
}

// ── SFY (Konfiguracja synchronizacji) ───────────────────────────────────────

export interface SfyConnectionContract {
	id: number;
	connectionId: number;
	contractId: number;
	/** Kontroluj/verify: kontrakt w snapshotach + overview. */
	sendActive: boolean;
	/** Swiadoma wysylka do klienta (apply). Bramka = sendActive AND pushActive. */
	pushActive: boolean;
	isMshrActive: boolean;
	pinnedBy?: number | null;
	createdAt: string;
	updatedAt: string;
}

export interface PinContractRequest {
	contractId: number;
	tenantSchema: string;
	sendActive?: boolean;
}

export interface SfyColumnMapping {
	sourceAlias: string;
	destColumn: string;
	isMatchKey: boolean;
	anonimize: boolean;
}

export interface SfyConnectionQuery {
	id: number;
	connectionId: number;
	name: string;
	ourTable: string;
	destinationTable: string;
	sourceSql: string;
	/** Archiwalny SQL ze starego systemu — tylko do przechowania/porownania. */
	sqlFarmasterOld?: string | null;
	columnMappings: SfyColumnMapping[];
	active: boolean;
	/** Swiadoma wysylka TEGO zapytania (apply). Bramka apply = kontrakt(send+push)
	 *  AND query.active AND query.pushActive. Default false — bezpiecznie. */
	pushActive: boolean;
	/** Wysylka AUTOMATYCZNA (nocny cron). Nadzbior pushActive — wymaga wysylki recznej. */
	autoApply: boolean;
	/** Ostatnia deska ratunku: prog, powyzej ktorego absurdalna jest sama SUMA. */
	autoMaxUpserts: number;
	/** GLOWNA bramka: ile wpisow moze przypasc na JEDNA apteke. Limit calkowity rosnie
	 *  z liczba aptek i alarmuje na normalnej nocy; ten mierzy anomalie tam, gdzie
	 *  ona siedzi. */
	autoMaxPerFirm: number;
	notes?: string | null;
	createdBy?: number | null;
	createdAt: string;
	updatedAt: string;
	aggregateSourceSql?: string | null;
	aggregateDestSql?: string | null;
	aggregateMatchKeys: string[];
	/** Tryb snapshotu dashboardu przegladu: 'off' | 'aggregate' | 'row'. */
	snapshotMode: SfySnapshotMode;
}

export type SfySnapshotMode = "off" | "aggregate" | "row" | "completeness";

export interface UpsertSfyQueryRequest {
	name: string;
	ourTable: string;
	destinationTable: string;
	sourceSql: string;
	sqlFarmasterOld?: string | null;
	columnMappings: SfyColumnMapping[];
	active: boolean;
	/** Swiadoma wysylka tego zapytania — default false. */
	pushActive: boolean;
	/** Wysylka automatyczna (cron) — default false. Wymaga pushActive. */
	autoApply: boolean;
	/** Limit calkowity automatu — ostatnia deska (default 20000). */
	autoMaxUpserts: number;
	/** Limit wpisow na aptekę — glowna bramka automatu (default 100). */
	autoMaxPerFirm: number;
	notes?: string | null;
	aggregateSourceSql?: string | null;
	aggregateDestSql?: string | null;
	aggregateMatchKeys: string[];
	snapshotMode: SfySnapshotMode;
}

export interface SfySqlAnalysis {
	placeholders: string[];
	aliases: string[];
}

export interface SfyFieldDifference {
	column: string;
	sourceValue?: string | null;
	destinationValue?: string | null;
}

export interface SfyRowDifference {
	matchKey: string[];
	differences: SfyFieldDifference[];
}

export interface SfyConsoleQueryResult {
	columns: string[];
	rows: (string | null)[][];
	rowCount: number;
	truncated: boolean;
	durationMs: number;
}

export interface SfyExplainReport {
	contractName: string;
	blozCount: number;
	unitcodeCount: number;
	substitutedSql: string;
	plan: string;
}

export interface SfyResolvedSql {
	contractName: string;
	blozCount: number;
	unitcodeCount: number;
	sql: string; // source_sql z podstawionymi placeholderami (do recznego uruchomienia)
}

export interface SfyVerificationReport {
	queryId: number;
	connectionId: number;
	contractId: number;
	contractName: string;
	tenantSchema: string;
	matchKeys: string[];
	sourceRowCount: number;
	destinationRowCount: number;
	matchedRowCount: number;
	missingInDestination: number;
	differentRows: number;
	sampleMissing: string[][];
	sampleDifferences: SfyRowDifference[];
	status: string;
	notes: string[];
}

export interface AggregateDiffSummary {
	missing: number; // liczba brakow u klienta (jest u nas, brak u klienta)
	different: number; // liczba roznic (klucz dopasowany, wartosci sie roznia)
	materialDifferent: number; // ile z `different` to roznice ISTOTNE (poza dgakt)
	/**
	 * Kubelki, ktore ma TYLKO klient. Poza `missing`/`different` z zasady: diff
	 * iteruje po NASZEJ stronie, wiec strukturalnie ich nie widzi. Nasza wysylka ich
	 * nie naprawi (INSERT/UPDATE, zero DELETE) — to informacja, nie zadanie.
	 */
	extra: number;
	/**
	 * Sumy miar po obu stronach z ostatniego snapshotu (`suma_ilosp`/`suma_ilzkl`/
	 * `suma_ilakt` + `liczba_wierszy`). `null` = tryb `row` albo snapshot sprzed v455.
	 * Sam licznik rozbieznosci nie mowi, CZY one znacza — te liczby mowia: 8990 roznic
	 * przy sumach zgodnych co do 0,03 % to szum, 100 roznic przy 30 % luki to pozar.
	 */
	sourceTotals: Record<string, number> | null;
	destTotals: Record<string, number> | null;
	computedAt: string | null; // ISO ostatniego UDANEGO przeliczenia, null gdy nigdy
	lastAttemptAt: string | null; // ISO ostatniej proby (sukces lub porazka)
	lastError: string | null; // blad ostatniej proby (null = OK)
}

/** Gotowy SQL do recznego uruchomienia + liczba wierszy. Zostal juz tylko dla
 *  „SQL kontrolny" (`getSfyApplySelect`). */
export interface SfyUpdateScript {
	sql: string;
	rowCount: number;
}

export interface SfyBucketReconcile {
	bucket: string[];
	kind: string; // missing | different
	expected: number | null;
	actual: number;
}

/**
 * Ksztalt zbioru do wyslania — do szybkiej oceny, czy to normalna dostawka, czy
 * naprawa zaleglosci. `dgaktMin` odpowiada na pytanie "jak stare rzeczy naprawiamy";
 * datzk/datsp by tu nie pomogly, bo towar potrafi lezec w aptece dwa lata.
 */
export interface SfyApplyScope {
	firms: number;
	/** Najwiekszy wklad pojedynczej apteki — srednia ukrywa jedna rozjechana apteke. */
	maxPerFirm: number;
	topFirm: string | null;
	dgaktMin: string | null;
	dgaktMax: string | null;
}

/** Podglad = apply (Etap 2): idempotentny keyed upsert detalu (INSERT-if-not-exists + UPDATE, zero DELETE). */
export interface SfyApplyScript {
	sql: string;
	statementCount: number;
	/** false => rozwiniety detal nie zgadza sie z agregatem — nie wysylac na slepo. */
	aligned: boolean;
	reconcile: SfyBucketReconcile[];
	mode: string; // aggregate | row
	notes: string[];
	/** Wiersze odsiane z braku bloz07 — NIE wchodza w statementCount. */
	skippedNoBloz: number;
	scope: SfyApplyScope;
	/** Wiersze pominiete, bo u klienta wygladaja identycznie (tylko tryb aggregate). */
	unchangedSkipped: number;
}

/** Wynik realnej wysylki/dry-run (Etap 3, run-log). */
export interface SfyApplyRunResult {
	runId: number;
	/** Czas calego przebiegu w ms (budowa statementow + polaczenie + wykonanie). */
	durationMs?: number;
	status: string; // dry_run | ok | failed | blocked
	dryRun: boolean;
	aligned: boolean;
	statementCount: number;
	appliedCount: number;
	/** Wiersze odsiane z braku bloz07 — poza statementCount. */
	skippedNoBloz: number;
	scope: SfyApplyScope;
	/** Wiersze pominiete, bo u klienta wygladaja identycznie (tylko tryb aggregate). */
	unchangedSkipped: number;
	error: string | null;
}

/** Rozklad roznic wg wyzerowanych partii (diagnostyka duzych wysylek kzak). */
export interface SfyIlaktZeroBreakdown {
	differentTotal: number;
	ilaktZeroed: number;
	hasIlakt: boolean;
	/**
	 * Gdy ustawione — licznika NIE DA SIE policzyc i to jest powod. Tryb `aggregate`
	 * trzyma w kubelku sumy, nie pojedyncze wiersze, wiec zwracanie 0 bylo falszywym
	 * uspokojeniem: UI mowil "0 ze 128 roznic to wyzerowane partie" dokladnie tam,
	 * gdzie ostrzezenie brzmi najgrozniej.
	 */
	notCountable: string | null;
}

/** Jeden wiersz detalu w kubelku: nasza strona vs klient. */
export interface SfyBucketDetailRow {
	matchKey: Record<string, string>;
	ours: Record<string, string | null> | null;
	client: Record<string, string | null> | null;
	differing: string[];
	status: "brak_u_klienta" | "roznica" | "nadmiar_u_klienta" | "zgodne";
	/**
	 * Ostatnia zmiana blozu na KARCIE TOWARU w tej aptece. `null` = takiego zdarzenia
	 * nie ma — i to jest informacja, nie brak danych: rozjazd blozu NIE pochodzi wtedy
	 * z apteki, tylko z naszej strony (defekt zrodla albo zmiana projekcji
	 * `k.bloz07` vs `t.bloz07`).
	 */
	blozChange: SfyBlozChange | null;
}

/** Zdarzenie zmiany blozu — podstawa decyzji, czy rozjazd propagowac do klienta. */
export interface SfyBlozChange {
	idfirm: number;
	idTowr: number | null;
	/** Klasyfikacja z `zmiany_blozu::TYP_SQL` — ta sama, ktorej uzywa raport i agent. */
	typ: "utrata" | "ponowne_uzycie" | "nadanie" | "przekodowanie";
	fromBloz07: string | null;
	toBloz07: string | null;
	fromNazwa: string | null;
	toNazwa: string | null;
	changeDate: string | null;
}

export interface SfyBucketDetail {
	bucket: Record<string, string>;
	kind: string;
	rows: SfyBucketDetailRow[];
	clientSql: string;
	truncated: boolean;
	missingCount: number;
	differentCount: number;
	extraCount: number;
	/**
	 * Waski skrypt naprawczy: UPDATE tylko na kolumnach, ktore sie roznia I ktore
	 * wolno nadpisac. Liczony razem ze szczegolami kubelka — zero dodatkowych zapytan.
	 */
	repair: SfyRepairScript;
}

/** Kontrakt FM1 do wyboru w porownaniu. Liczniki, bo same nazwy bywaja identyczne
 *  miedzy okresami i bez nich wybor z listy jest zgadywanka. */
export interface Fm1ContractRow {
	id: number;
	name: string | null;
	manufacturerId: number | null;
	rozliczenie: boolean | null;
	apteki: number;
	blozy: number;
}

/** Roznica dwoch zbiorow — probki obciete, liczniki pelne. */
export interface SfySetDiff {
	onlyFm1: string[];
	onlyFm2: string[];
	onlyFm1Count: number;
	onlyFm2Count: number;
	common: number;
	truncated: boolean;
}

export interface DokfLineRow {
	idtowr: number;
	/** Bloz wpisany w POZYCJE zakupu. */
	kzakBloz07: string | null;
	/** Bloz z KARTOTEKI apteki — po nim liczy sie zakres kontraktu. */
	towrBloz07: string | null;
	nazwa: string | null;
	datzk: string | null;
	ilakt: number | null;
	inContract: boolean;
}

export interface DokfLinesReport {
	iddokf: number;
	idfirm: number;
	rows: DokfLineRow[];
	inContractCount: number;
	/** Ile pozycji ma rozjazd `k.bloz07` vs `t.bloz07`. */
	mismatchCount: number;
	/** Okno `datzk`, w ktorym szukalismy — zaweza wynik, wiec pokazujemy je. */
	datzkFrom: string;
	datzkTo: string;
	sql: string;
}

export interface Fm1Comparison {
	fm2ContractName: string;
	fm1ContractId: number;
	fm1ContractName: string | null;
	pharmacies: SfySetDiff;
	blozy: SfySetDiff;
	/** Rzeczy, ktore trzeba wiedziec, zeby nie czytac liczb blednie. */
	notes: string[];
}

/** Waska naprawa u klienta — `skipped` nigdy nie jest ciche. */
export interface SfyRepairScript {
	sql: string;
	statementCount: number;
	skipped: string[];
}

/** Apteka z liczba rozbieznosci — do wysylki partiami. */
export interface SfyFlaggedFirm {
	idfirm: number;
	diffs: number;
}

/** Wiersz historii wysylek (audyt outbound_apply_runs). */
export interface SfyApplyRun {
	id: number;
	dryRun: boolean;
	aligned: boolean;
	status: string; // dry_run | running | ok | failed | blocked
	statementCount: number;
	appliedCount: number;
	/** Wiersze odsiane z braku bloz07 — poza statementCount. */
	skippedNoBloz: number;
	error: string | null;
	runBy: number | null;
	/** Apteka, do ktorej zawezono wysylke; null = pelny zakres kontraktu. */
	scopeIdfirm: number | null;
	startedAt: string;
	finishedAt: string | null;
}

/** Jeden bloz w porownaniu stanow kzak. */
export interface SfyKzakStockRow {
	bloz07: string;
	/** SUM(ilakt). UWAGA: 0 znaczy „mamy wiersze, ale suma zerowa" — co innego niz null. */
	ours: number | null;
	client: number | null;
	/** Liczba wierszy kzak po obu stronach — odroznia „suma 0" od „brak wierszy". */
	oursRows: number;
	clientRows: number;
	diff: number | null;
	inContract: boolean;
	status:
		| "brak_u_klienta"
		| "rozjazd_ilosci"
		| "nadmiar_kontraktowy"
		| "nadmiar_spoza_kontraktu"
		| "ok";
}

export interface SfyKzakStockComparison {
	rows: SfyKzakStockRow[];
	ourSql: string;
	clientSql: string;
	missingCount: number;
	extraCount: number;
	diffCount: number;
	okCount: number;
	ourQueryMs: number;
	clientQueryMs: number;
	/** Zastosowane okno w miesiacach; null = cala historia. */
	monthsBack: number | null;
}

/** Sonda „co klient ma dla tej apteki" — jedna tabela klienta. */
export interface SfyPharmacyProbeRow {
	ourTable: string;
	destinationTable: string;
	sql: string;
	minDgakt: string | null;
	maxDgakt: string | null;
	rowCount: number | null;
	error: string | null;
}

// "Sprawdź aptekę" — kompletnosc jednej apteki we wszystkich zapytaniach.
export interface SfyPharmacyCheckRow {
	queryName: string;
	ourTable: string;
	mode: string; // "aggregate" | "row"
	sourceCount: number; // count(*) u nas (row: wiersze; aggregate: grupy)
	destCount: number; // count(*) u klienta
	matched: number;
	missing: number;
	different: number;
	status: "ok" | "rozni_sie" | "blad";
	error: string | null;
	/**
	 * Zapytania, ktore REALNIE poszly. Zapisany `source_sql` to tylko KOPIA szablonu —
	 * dopiero tekst z podstawionymi placeholderami mowi, na jakim zakresie te liczby
	 * powstaly. `null` przy bledzie albo w trybie completeness.
	 */
	ourSql: string | null;
	clientSql: string | null;
}

export interface SfyPharmacyCheckReport {
	rows: SfyPharmacyCheckRow[];
	okCount: number;
	diffCount: number;
	errorCount: number;
}

/**
 * Jedna miara kontraktu: nasza suma vs suma klienta. Trzy miary (stan / zakup /
 * sprzedaz) to trzy istniejace agregaty — kazdy z IDENTYCZNYM filtrem po obu
 * stronach, wiec porownanie jest jablko do jablka.
 */
export interface SfyMeasureTotal {
	measure: "stan" | "zakup" | "sprzedaz";
	label: string;
	/** Zapytanie, ktorego agregat te miare MIERZY (jego filtr jest jej definicja). */
	measuredBy: string;
	/** Kolumna miary — widac, co dokladnie sumujemy. */
	column: string;
	/**
	 * Z JAKIEGO zakresu liczona jest ta miara — odczytane z placeholderow jej agregatu.
	 * Rozstrzyga „zmienilem kontrakt z Q na H, czemu liczby stoja": `stan` idzie za
	 * oknem 2 lat, nie za kontraktem, a agregat z datami zaszytymi w SQL nie zareaguje
	 * na zmiane okresu w ogole.
	 */
	range: string;
	ours: number | null;
	client: number | null;
	/**
	 * Wartosc miary po NASZYCH cenach ex-factory (cenniki podpiete do kontraktu),
	 * liczona z tych samych kubelkow agregatu po obu stronach. `null` dla stanu
	 * i dla snapshotu sprzed tej funkcji.
	 */
	oursExfactoryValue: number | null;
	clientExfactoryValue: number | null;
	/** Produkty z ruchem bez ceny w podpietych cennikach — wartosc jest wtedy zanizona. */
	oursMissingPriceCount: number | null;
	clientMissingPriceCount: number | null;
	computedAt: string | null;
	/**
	 * Zapytania zasilajace TE SAMA tabele klienta. Miare mierzy JEDNO, a zmienic ja
	 * moze KAZDE z nich — do kzak pisza kzak_stan, kzak_sprz i kzak_korz naraz.
	 */
	fedBy: string[];
}

/** „Sprawdź bloz" — czy dany produkt jest u klienta we wszystkich tabelach kontraktu. */
export interface SfyBlozCheckReport {
	bloz07: string;
	/**
	 * Czy bloz w ogole NALEZY do kontraktu. `false` => zera w `rows` byłyby poprawne,
	 * a nie objawem awarii — dlatego wtedy w ogole nie odpytujemy zrodel. To najczestsza
	 * odpowiedz na „apteka ma nowy bloz".
	 */
	inContract: boolean;
	contractBlozCount: number;
	/** Nazwa z kartoteki — sam bloz nic czlowiekowi nie mowi. */
	productName: string | null;
	/** W ilu aptekach mamy KARTE tego produktu (towr), niezaleznie od kontraktu. */
	firmsWithCard: number;
	rows: SfyPharmacyCheckRow[];
	okCount: number;
	diffCount: number;
	errorCount: number;
	/**
	 * Zapytania POMINIETE, bo ich filtr nie zna blozu (`firm`, `dedf` — filtruja
	 * wylacznie po aptekach). Puszczone i tak zwracalyby pelny zakres kontraktu
	 * i wygladalyby na odpowiedz o produkcie, ktora nia nie jest.
	 */
	skippedNoBlozScope: string[];
}

export interface AggregateDiffRow {
	id: number;
	kind: "missing" | "different" | "extra";
	matchKey: Record<string, string>; // np. {"idfirm":"855","idtowr":"271","miesiac":"2026-06"}
	sourceValues: Record<string, string | null>; // np. {"suma_ilosp":"37","liczba_wierszy":"27"}
	destValues: Record<string, string | null> | null; // null dla kind=missing
	diffColumns: string[]; // kolumny ktore realnie sie roznia (wg cells_equal); puste dla missing
	/**
	 * `towr.bloz07` — prawda o produkcie z kartoteki apteki, po (idfirm, idtowr).
	 * Wiersz niesie `k.bloz07` (to, co fizycznie lezy u klienta), a zakres kontraktu
	 * liczy sie po `t.bloz07`; ta kolumna pokazuje obie strony obok siebie.
	 * `null` gdy zapytanie nie projektuje `idtowr` (agregat) albo brak wpisu w towr.
	 */
	towrBloz07: string | null;
	computedAt: string;
}

/** Biezacy kontrakt nieprzypiety do polaczenia, ktore juz wysyla dane producenta. */
export interface SfyUnpinnedContract {
	producerId: number | null;
	producerName: string | null;
	connectionId: number;
	connectionName: string;
	contractId: number;
	contractName: string;
	year: number;
	quarter: string;
}

/** Jeden wiersz macierzy przegladu SFY (producent × tabelka × kontrakt). */
export interface SfyOverviewRow {
	producerId: number | null;
	producerName: string | null;
	connectionId: number;
	queryId: number;
	queryName: string;
	ourTable: string;
	snapshotMode: SfySnapshotMode;
	contractId: number;
	contractName: string;
	year: number;
	quarter: string;
	missing: number;
	different: number;
	/** Ile z `different` to roznice ISTOTNE (poza dgakt); z ostatniego snapshotu. */
	materialDifferent: number;
	/** ISO ostatniego UDANEGO snapshotu; null gdy nigdy nie przeliczono. */
	computedAt: string | null;
	/** ISO ostatniej PROBY (sukces lub porazka). */
	lastAttemptAt: string | null;
	/** Blad ostatniej proby (null = OK); np. statement timeout w nocnym cronie. */
	lastError: string | null;
	/** Ostatnia REALNA wysylka (dry_run=false): kiedy, status, ile upsertow wykonano/zaplanowano. */
	lastApplyAt?: string | null;
	lastApplyStatus?: string | null;
	lastApplyApplied?: number | null;
	lastApplyStatements?: number | null;
	/** Powod niepowodzenia/blokady ostatniej wysylki (np. limit automatu). */
	lastApplyError?: string | null;
	/** Czy zapytanie jest dopuszczone do wysylki (zgoda reczna). */
	queryPushActive?: boolean;
	/** Czy wysylka leci automatem (cron) — wymaga takze wysylki na kontrakcie. */
	autoApply?: boolean;
	autoMaxUpserts?: number;
	/** Czy KONTRAKT ma wlaczona wysylke — bez tego automat nie wystartuje. */
	contractPushActive?: boolean;
	/** Ilu roznych idfirm (aptek) dotycza BRAKI. 0 gdy brak brakow / tabela bez idfirm w kluczu. */
	missingFirms?: number | null;
	/** Ile roznych blozow liczy kontrakt (wszystkie koszyki) — lista `:BLOZY` zapytan SFY. */
	contractBlozCount?: number;
	/**
	 * Najstarszy `dgakt` wsrod BRAKUJACYCH wierszy — jak gleboko siega luka.
	 * `null` w trybie agregatu (nie ma tam kolumny dgakt) oraz gdy brakow nie ma.
	 */
	missingMinDgakt?: string | null;
	/** To samo dla wierszy ROZNIACYCH SIE — liczone osobno od brakow. */
	differentMinDgakt?: string | null;
}

export const outboundApi = {
	listClients: (activeOnly = false) =>
		apiFetch<OutboundClient[]>(`/outbound/clients?activeOnly=${activeOnly}`),

	listProducers: () => apiFetch<OutboundProducer[]>("/outbound/producers"),

	createClientForProducer: (producerId: number) =>
		apiFetch<OutboundClient>("/outbound/clients", {
			method: "POST",
			body: JSON.stringify({ producerId }),
		}),

	updateClientProducer: (clientId: number, producerId?: number | null) =>
		apiFetch<OutboundClient>(`/outbound/clients/${clientId}`, {
			method: "PATCH",
			body: JSON.stringify({ producerId }),
		}),

	listConnections: (activeOnly = true, contractId?: number) => {
		const params = new URLSearchParams();
		params.set("activeOnly", String(activeOnly));
		if (contractId != null) {
			params.set("contractId", String(contractId));
		}
		return apiFetch<OutboundConnection[]>(`/outbound/connections?${params.toString()}`);
	},

	getConnection: (connectionId: number) =>
		apiFetch<OutboundConnection>(`/outbound/connections/${connectionId}`),

	createConnection: (payload: UpsertOutboundConnectionRequest) =>
		apiFetch<OutboundConnection>("/outbound/connections", {
			method: "POST",
			body: JSON.stringify(payload),
		}),

	updateConnection: (connectionId: number, payload: UpsertOutboundConnectionRequest) =>
		apiFetch<OutboundConnection>(`/outbound/connections/${connectionId}`, {
			method: "PATCH",
			body: JSON.stringify(payload),
		}),

	updateConnectionNotes: (connectionId: number, notes: string | null) =>
		apiFetch<OutboundConnection>(`/outbound/connections/${connectionId}/notes`, {
			method: "PATCH",
			body: JSON.stringify({ notes }),
		}),

	deleteConnection: (connectionId: number) =>
		apiFetch<{ status: string }>(`/outbound/connections/${connectionId}`, {
			method: "DELETE",
		}),

	testConnection: (connectionId: number) =>
		apiFetch<OutboundConnectionTestResult>(`/outbound/connections/${connectionId}/test`, {
			method: "POST",
		}),

	getConnectionSchema: (connectionId: number) =>
		apiFetch<OutboundSchemaSnapshot>(`/outbound/connections/${connectionId}/schema`),

	listVerificationRuns: (connectionId: number, contractId: number, limit = 20) =>
		apiFetch<OutboundVerificationRunRow[]>(
			`/outbound/connections/${connectionId}/verification-runs?contractId=${contractId}&limit=${limit}`,
		),

	getVerificationRun: (runId: number) =>
		apiFetch<OutboundVerificationRunDetail>(`/outbound/verification-runs/${runId}`),

	runHerbapolVerification: (
		connectionId: number,
		contractId: number,
		payload: RunHerbapolVerificationRequest,
	) =>
		apiFetch<OutboundVerificationRunDetail>(
			`/outbound/connections/${connectionId}/herbapolp/contracts/${contractId}/run-verification`,
			{
				method: "POST",
				body: JSON.stringify(payload),
			},
		),

	// ── SFY ────────────────────────────────────────────────────────────────────

	listSfyContracts: (connectionId: number) =>
		apiFetch<SfyConnectionContract[]>(`/outbound/connections/${connectionId}/contracts`),

	pinSfyContract: (connectionId: number, payload: PinContractRequest) =>
		apiFetch<SfyConnectionContract>(`/outbound/connections/${connectionId}/contracts`, {
			method: "POST",
			body: JSON.stringify(payload),
		}),

	updateSfyContract: (
		connectionId: number,
		contractId: number,
		patch: { sendActive?: boolean; pushActive?: boolean; isMshrActive?: boolean },
	) =>
		apiFetch<SfyConnectionContract>(
			`/outbound/connections/${connectionId}/contracts/${contractId}`,
			{
				method: "PATCH",
				body: JSON.stringify(patch),
			},
		),

	unpinSfyContract: (connectionId: number, contractId: number) =>
		apiFetch<{ status: string }>(`/outbound/connections/${connectionId}/contracts/${contractId}`, {
			method: "DELETE",
		}),

	listSfyQueries: (connectionId: number) =>
		apiFetch<SfyConnectionQuery[]>(`/outbound/connections/${connectionId}/queries`),

	createSfyQuery: (connectionId: number, payload: UpsertSfyQueryRequest) =>
		apiFetch<SfyConnectionQuery>(`/outbound/connections/${connectionId}/queries`, {
			method: "POST",
			body: JSON.stringify(payload),
		}),

	updateSfyQuery: (connectionId: number, queryId: number, payload: UpsertSfyQueryRequest) =>
		apiFetch<SfyConnectionQuery>(`/outbound/connections/${connectionId}/queries/${queryId}`, {
			method: "PATCH",
			body: JSON.stringify(payload),
		}),

	deleteSfyQuery: (connectionId: number, queryId: number) =>
		apiFetch<{ status: string }>(`/outbound/connections/${connectionId}/queries/${queryId}`, {
			method: "DELETE",
		}),

	analyzeSfySql: (sourceSql: string) =>
		apiFetch<SfySqlAnalysis>("/outbound/sfy/analyze-sql", {
			method: "POST",
			body: JSON.stringify({ sourceSql }),
		}),

	runSfyConsoleQuery: (connectionId: number, sql: string, limit: number) =>
		apiFetch<SfyConsoleQueryResult>(`/outbound/connections/${connectionId}/console/query`, {
			method: "POST",
			body: JSON.stringify({ sql, limit }),
		}),

	runSfyVerification: (connectionId: number, queryId: number, contractId: number, full = false) =>
		apiFetch<SfyVerificationReport>(
			`/outbound/connections/${connectionId}/queries/${queryId}/verify?contractId=${contractId}${full ? "&full=true" : ""}`,
			{ method: "POST" },
		),

	explainSfyQuery: (connectionId: number, queryId: number, contractId: number) =>
		apiFetch<SfyExplainReport>(
			`/outbound/connections/${connectionId}/queries/${queryId}/explain?contractId=${contractId}`,
			{ method: "POST" },
		),

	getSfyResolvedSql: (connectionId: number, queryId: number, contractId: number) =>
		apiFetch<SfyResolvedSql>(
			`/outbound/connections/${connectionId}/queries/${queryId}/resolved-sql?contractId=${contractId}`,
		),

	/** Porownanie stanow kzak (SUM(ilakt) per bloz07) dla jednej apteki: nasza strona
	 *  zawezona do blozow kontraktu vs klient BEZ filtra blozow. */
	compareSfyKzakStock: (
		connectionId: number,
		contractId: number,
		idfirm: number,
		/** Okno w miesiacach wstecz, symetryczne dla obu stron. 0 = cala historia. */
		monthsBack: number,
	) =>
		apiFetch<SfyKzakStockComparison>(
			`/outbound/connections/${connectionId}/kzak-stock-compare?contractId=${contractId}&idfirm=${idfirm}&monthsBack=${monthsBack}`,
		),

	/** „Co klient ma dla tej apteki" — min/max dgakt + liczba wierszy w sprz/kzak/firm/towr
	 *  po stronie klienta. Tylko odczyt. */
	probeSfyPharmacy: (connectionId: number, idfirm: number) =>
		apiFetch<SfyPharmacyProbeRow[]>(
			`/outbound/connections/${connectionId}/probe-pharmacy?idfirm=${idfirm}`,
		),

	// "Sprawdź aptekę" — wszystkie aktywne zapytania polaczenia zawezone do idfirm.
	checkSfyPharmacy: (connectionId: number, contractId: number, idfirm: number) =>
		apiFetch<SfyPharmacyCheckReport>(
			`/outbound/connections/${connectionId}/check-pharmacy?contractId=${contractId}&idfirm=${idfirm}`,
		),

	/**
	 * Pozycje zakupowe JEDNEGO dokumentu: `k.bloz07` (pozycja) vs `t.bloz07`
	 * (kartoteka). Zakres kontraktu liczy sie po tym drugim, wiec rozjazd miedzy
	 * nimi jest typowym zrodlem rozbieznosci w SFY.
	 */
	getDokfLines: (connectionId: number, contractId: number, idfirm: number, iddokf: number) =>
		apiFetch<DokfLinesReport>(
			`/outbound/connections/${connectionId}/dokf-lines?contractId=${contractId}&idfirm=${idfirm}&iddokf=${iddokf}`,
		),

	/** Odbiorcy nocnego podsumowania SFY. Pusta lista = mail wylaczony. */
	getSfySummaryRecipients: () => apiFetch<string[]>("/outbound/sfy/summary-recipients"),

	setSfySummaryRecipients: (recipients: string[]) =>
		apiFetch<string[]>("/outbound/sfy/summary-recipients", {
			method: "PUT",
			body: JSON.stringify({ recipients }),
		}),

	/** Kontrakty FM1 do porownania (nieskasowane, z licznikami aptek i blozow). */
	listFm1Contracts: () => apiFetch<Fm1ContractRow[]>("/outbound/sfy/fm1-contracts"),

	/**
	 * Porownanie aptek i blozow kontraktu FM2 z kontraktem FM1.
	 *
	 * `contractId2` jest opcjonalne: w FM1 producent mial jeden kontrakt, a FM2
	 * rozdziela go na apteki wlasne (AW) i franczyzowe (AF). Bez drugiej strony
	 * apteki z niej wygladaja na „nie przenieslismy".
	 */
	compareWithFm1: (
		contractId: number,
		fm1ContractId: number,
		contractId2?: number,
		/** `true` = pelne listy roznic (eksport). Na ekran idzie probka do 200 pozycji. */
		full?: boolean,
	) =>
		apiFetch<Fm1Comparison>(
			`/outbound/sfy/fm1-compare?contractId=${contractId}&fm1ContractId=${fm1ContractId}` +
				(contractId2 ? `&contractId2=${contractId2}` : "") +
				(full ? "&full=true" : ""),
		),

	/** Sumy miar kontraktu (stan/zakup/sprzedaz) z ostatniego snapshotu. Tylko odczyt. */
	getSfyMeasureTotals: (connectionId: number, contractId: number) =>
		apiFetch<SfyMeasureTotal[]>(
			`/outbound/connections/${connectionId}/measure-totals?contractId=${contractId}`,
		),

	/** „Sprawdź bloz" — jeden produkt przez wszystkie aktywne zapytania kontraktu.
	 *  `idfirm` opcjonalny: bez niego caly kontrakt, z nim jedna apteka. */
	checkSfyBloz: (connectionId: number, contractId: number, bloz07: string, idfirm?: number) =>
		apiFetch<SfyBlozCheckReport>(
			`/outbound/connections/${connectionId}/check-bloz?contractId=${contractId}&bloz07=${encodeURIComponent(
				bloz07,
			)}${idfirm != null ? `&idfirm=${idfirm}` : ""}`,
		),

	/** SELECT po NASZEJ stronie zawezony do kluczy z wysylki — do recznego sprawdzenia
	 *  na bazie PG zanim cokolwiek poleci do klienta. */
	getSfyApplySelect: (
		connectionId: number,
		queryId: number,
		contractId: number,
		/** Zawezenie kontroli do jednej apteki (idfirm). */
		idfirm?: number,
	) =>
		apiFetch<SfyUpdateScript>(
			`/outbound/connections/${connectionId}/queries/${queryId}/apply-select?contractId=${contractId}${
				idfirm != null ? `&idfirm=${idfirm}` : ""
			}`,
		),

	// Podglad = apply: idempotentny upsert detalu. Jedyny podglad "co doslac" —
	// agregatowe insert/update-script zostaly usuniete (liczyly na kubelkach, wiec
	// pokazywaly co innego niz to, co realnie leci).
	getSfyApplyScript: (
		connectionId: number,
		queryId: number,
		contractId: number,
		/** Podglad zawezony do jednej apteki. Realna wysylka zostaje pelnozakresowa. */
		idfirm?: number,
	) =>
		apiFetch<SfyApplyScript>(
			`/outbound/connections/${connectionId}/queries/${queryId}/apply-script?contractId=${contractId}${
				idfirm != null ? `&idfirm=${idfirm}` : ""
			}`,
		),

	// Realny apply / dry-run (Etap 3). dryRun=true nie dotyka klienta.
	applySfy: (
		connectionId: number,
		queryId: number,
		contractId: number,
		dryRun: boolean,
		/** Zawezenie wysylki do jednej apteki — ta sama sciezka i bramki co pelna. */
		idfirm?: number,
	) =>
		apiFetch<SfyApplyRunResult>(
			`/outbound/connections/${connectionId}/queries/${queryId}/apply?contractId=${contractId}&dryRun=${dryRun}${
				idfirm != null ? `&idfirm=${idfirm}` : ""
			}`,
			{ method: "POST" },
		),

	/** Ile roznic to wyzerowanie partii (ilakt 0 u nas, niezerowy u klienta). Na zadanie. */
	getSfyIlaktZero: (connectionId: number, queryId: number, contractId: number) =>
		apiFetch<SfyIlaktZeroBreakdown>(
			`/outbound/connections/${connectionId}/queries/${queryId}/ilakt-zero?contractId=${contractId}`,
		),

	/** Wejscie w kubelek agregatu: konkretne wiersze + nadmiary u klienta. */
	getSfyBucketDetail: (connectionId: number, queryId: number, contractId: number, diffId: number) =>
		apiFetch<SfyBucketDetail>(
			`/outbound/connections/${connectionId}/queries/${queryId}/bucket-detail?contractId=${contractId}&diffId=${diffId}`,
		),

	/** Apteki, ktore maja co wyslac — podstawa wysylki partiami (apteka po aptece). */
	getSfyFlaggedFirms: (connectionId: number, queryId: number, contractId: number) =>
		apiFetch<SfyFlaggedFirm[]>(
			`/outbound/connections/${connectionId}/queries/${queryId}/flagged-firms?contractId=${contractId}`,
		),

	// Historia wysylek (run-log audytu) dla komorki przegladu.
	getSfyApplyRuns: (connectionId: number, queryId: number, contractId: number) =>
		apiFetch<SfyApplyRun[]>(
			`/outbound/connections/${connectionId}/queries/${queryId}/apply-runs?contractId=${contractId}`,
		),

	runSfyAggregateVerification: (connectionId: number, queryId: number, contractId: number) =>
		apiFetch<SfyVerificationReport>(
			`/outbound/connections/${connectionId}/queries/${queryId}/verify-aggregate?contractId=${contractId}`,
			{ method: "POST" },
		),

	runSfyAggregateSnapshot: (connectionId: number, queryId: number, contractId: number) =>
		apiFetch<AggregateDiffSummary>(
			`/outbound/connections/${connectionId}/queries/${queryId}/aggregate-snapshot?contractId=${contractId}`,
			{ method: "POST" },
		),

	/** Uniwersalny snapshot — backend dispatch po snapshotMode (row|aggregate). */
	runSfySnapshot: (connectionId: number, queryId: number, contractId: number) =>
		apiFetch<AggregateDiffSummary>(
			`/outbound/connections/${connectionId}/queries/${queryId}/snapshot?contractId=${contractId}`,
			{ method: "POST" },
		),

	/** Macierz przegladu SFY „stan na dzis"; producerId zawezа do producenta. */
	getSfyUnpinnedCurrentContracts: () =>
		apiFetch<SfyUnpinnedContract[]>("/outbound/sfy/unpinned-current-contracts"),

	getSfyOverview: (producerId?: number) =>
		apiFetch<SfyOverviewRow[]>(
			`/outbound/sfy/overview${producerId != null ? `?producerId=${producerId}` : ""}`,
		),

	getSfyAggregateDiffsSummary: (connectionId: number, queryId: number, contractId: number) =>
		apiFetch<AggregateDiffSummary>(
			`/outbound/connections/${connectionId}/queries/${queryId}/aggregate-diffs/summary?contractId=${contractId}`,
		),

	listSfyAggregateDiffs: (
		connectionId: number,
		queryId: number,
		contractId: number,
		opts?: { kind?: "missing" | "different" | "extra"; limit?: number; offset?: number },
	) => {
		const params = new URLSearchParams({ contractId: String(contractId) });
		if (opts?.kind) params.set("kind", opts.kind);
		if (opts?.limit != null) params.set("limit", String(opts.limit));
		if (opts?.offset != null) params.set("offset", String(opts.offset));
		return apiFetch<AggregateDiffRow[]>(
			`/outbound/connections/${connectionId}/queries/${queryId}/aggregate-diffs?${params.toString()}`,
		);
	},
};
